import { useCallback, useEffect, useState } from 'react';
import { MessageBus, Event } from '@podium/browser';
import { ContextStats } from '../fetchContextStats';

const messageBus = new MessageBus();

type FilterState =
    | { state: 'initial'; contextStats: ContextStats }
    | { state: 'pending' }
    | { state: 'success'; contextStats: ContextStats }
    | { state: 'error'; errorMessage: string }

const initialState = (contextStats: ContextStats): FilterState => ({
    state: 'initial',
    contextStats
});

export function useFilterUpdated(initialData: ContextStats | null, apiUrl: string) {
    const [filterState, setFilterState] = useState<FilterState>(
        initialData == null ? { state: 'pending' } : initialState(initialData)
    );

    const fetchData = useCallback(async (message: Event) => {
        setFilterState({ state: 'pending' });
        const searchParams = createQueryString(message.payload as object);
        if (searchParams.get('location') === null || searchParams.get('location') === undefined) {
            setFilterState({
                state: 'error',
                errorMessage: 'Failed fetching from the server'
            });
            return;
        }
        try {
            const url = `${apiUrl}?${searchParams.toString()}`;
            const response = await fetch(url);
            const apiData = await response.json();

            if (response.ok && apiData.data && apiData.message === 'OK') {
                setFilterState({ state: 'success', contextStats: apiData });
            } else {
                setFilterState({
                    state: 'error',
                    errorMessage: 'Failed fetching from the server'
                });
            }
        } catch (err) {
            const error = err as Error;
            setFilterState({ state: 'error', errorMessage: error.message });
        }
    }, []);

    const callback = useCallback(
        (event: Event) => {
            fetchData(event);
        },
        [fetchData]
    );

    useEffect(() => {
        setTimeout(() => {
            if (initialData && initialData.data && initialData.message === 'OK')
                setFilterState({ state: 'success', contextStats: initialData });
        }, 10);

        messageBus.subscribe('filter', 'updated', callback);
        return () => {
            messageBus.unsubscribe('filter', 'updated', callback);
        };
    }, [callback]);

    return filterState;
}

function createQueryString(queryParams: object): URLSearchParams {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, item));
        } else {
            searchParams.append(key, value);
        }
    });
    return searchParams;
}
